import { Link, useNavigate } from "react-router-dom";
import { useAuth, useCart } from "@/App";
import { toast } from "sonner";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function CartPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cart, updateCartItem, removeFromCart } = useCart();

  const handleUpdateQuantity = async (item, newQty) => {
    if (newQty < 1) return;
    try {
      await updateCartItem(item.product_id, newQty, item.size, item.color);
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const handleRemove = async (item) => {
    try {
      await removeFromCart(item.product_id, item.size, item.color);
      toast.success("Item removed from cart");
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const handleCheckout = () => {
    if (!user) {
      toast.error("Please sign in to checkout");
      navigate("/auth");
      return;
    }
    navigate("/checkout");
  };

  return (
    <div className="min-h-screen pt-32 pb-24 px-4 sm:px-6 lg:px-8" data-testid="cart-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-end justify-between mb-12">
          <div>
            <span className="label-text text-[#39FF14] text-sm tracking-[0.3em]">YOUR BAG</span>
            <h1 className="mt-2 text-4xl heading text-[#EDEDED]">Shopping Cart</h1>
          </div>
          {cart.items.length > 0 && (
            <span className="text-[#888]">{cart.items.length} item{cart.items.length !== 1 ? "s" : ""}</span>
          )}
        </div>

        {cart.items.length === 0 ? (
          <div className="text-center py-24">
            <ShoppingBag size={64} className="mx-auto text-[#333] mb-6" />
            <p className="text-[#888] text-xl mb-6">Your cart is empty</p>
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase"
              data-testid="continue-shopping-btn"
            >
              <span>Start Shopping</span>
              <ArrowRight size={20} className="transform skewX-[12deg]" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-6">
              {cart.items.map((item, index) => (
                <div
                  key={`${item.product_id}-${item.size}-${item.color}`}
                  className="flex gap-4 p-4 bg-[#0A0A0A] border border-[#222]"
                  data-testid={`cart-item-${index}`}
                >
                  {/* Image */}
                  <Link to={`/product/${item.product_id}`} className="w-24 h-24 flex-shrink-0">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/product/${item.product_id}`}
                      className="text-[#EDEDED] font-semibold hover:text-[#39FF14] transition-colors"
                    >
                      {item.name}
                    </Link>
                    <div className="mt-1 text-[#888] text-sm">
                      {item.size && <span>Size: {item.size}</span>}
                      {item.size && item.color && <span className="mx-2">•</span>}
                      {item.color && <span>Color: {item.color}</span>}
                    </div>
                    <p className="mt-2 text-[#39FF14] font-bold heading">
                      ${item.price.toFixed(2)}
                    </p>

                    {/* Quantity & Remove */}
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                          className="qty-btn w-8 h-8"
                          data-testid={`cart-item-${index}-decrease`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-[#EDEDED] w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                          className="qty-btn w-8 h-8"
                          data-testid={`cart-item-${index}-increase`}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemove(item)}
                        className="text-[#888] hover:text-red-500 transition-colors p-2"
                        data-testid={`cart-item-${index}-remove`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-[#0A0A0A] border border-[#222] p-6 sticky top-32">
                <h3 className="text-xl heading text-[#EDEDED] mb-6">Order Summary</h3>

                <div className="space-y-4 pb-6 border-b border-[#222]">
                  <div className="flex justify-between text-[#888]">
                    <span>Subtotal</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[#888]">
                    <span>Shipping</span>
                    <span>{cart.total >= 100 ? "Free" : "$9.99"}</span>
                  </div>
                </div>

                <div className="flex justify-between py-6 text-lg">
                  <span className="text-[#EDEDED] heading">Total</span>
                  <span className="text-[#39FF14] heading" data-testid="cart-total">
                    ${(cart.total + (cart.total >= 100 ? 0 : 9.99)).toFixed(2)}
                  </span>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full btn-skew bg-[#39FF14] text-black px-8 py-4 font-bold uppercase tracking-wider animate-pulse-neon"
                  data-testid="checkout-btn"
                >
                  <span>Proceed to Checkout</span>
                </button>

                <p className="mt-4 text-center text-[#888] text-sm">
                  {cart.total < 100 && `Add $${(100 - cart.total).toFixed(2)} more for free shipping`}
                </p>

                <Link
                  to="/shop"
                  className="mt-6 flex items-center justify-center gap-2 text-[#888] hover:text-[#39FF14] transition-colors"
                >
                  <span className="label-text">Continue Shopping</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
